import { Injectable } from "@angular/core";
import { CacheKeyEnum, ListState } from "./list-state-cache.types";

/**
 * Keys whose state survives a page reload (SPEC-2026-08-crm-pipeline-deals-radar — RF-19).
 *
 * Persistence is OPT-IN PER KEY on purpose. This service is shared by 12 components
 * (attendances, contacts, users, tags, reports, tracking, and the admin app), and
 * their state includes whole `Pager` payloads. Persisting every key would serialise
 * entire contact and attendance pages into localStorage — a quota problem and a
 * stale-data problem at once. Only `search`, `params` and `extraData` are stored, never
 * `pager` or `lists`: the CRM filters keep their chip order in `extraData`, and their
 * restore reads it (SPEC-2026-10-crm-kanban-saved-filters RF-26).
 */
const PERSISTED_KEYS: ReadonlyArray<string> = [CacheKeyEnum.CRM_FILTERS];

const STORAGE_PREFIX = 'trendmulti.listState.';

@Injectable({ providedIn: 'root' })
export class ListStateCacheService {

  private cache = new Map<string, ListState<any>>();

  /**
   * Scope of a persisted entry. The CRM filters are per user AND per funnel: two
   * salespeople on the same browser must not inherit each other's board, and
   * switching funnels must not carry the previous funnel's filters over.
   */
  private scope = '';

  /** Called when the logged user or the selected funnel changes. */
  setScope(scope: string): void {
    const next = scope ?? '';
    if (next === this.scope) return;

    this.scope = next;

    // The in-memory Map is keyed by `CacheKeyEnum` alone, so a scope change has to
    // drop the persisted keys' entries: otherwise `get()` answers the new scope out
    // of the previous scope's memory and never reaches its own stored state — funnel
    // B would inherit funnel A's filters and then persist them as its own.
    PERSISTED_KEYS.forEach((key) => this.cache.delete(key));
  }

  set<T>(key: string, state: ListState<T>): void {
    this.cache.set(key, structuredClone(state));
    this.persist(key, state);
  }

  get<T>(key: string): ListState<T> | null {
    const state = this.cache.get(key);
    if (state) return structuredClone(state);

    // Nothing in memory: this is a fresh page load, so fall back to storage for the
    // keys that opted in (RF-19, AC-07).
    return this.restore<T>(key);
  }

  clear(key: string): void {
    this.cache.delete(key);
    this.forget(key);
  }

  clearAll(): void {
    this.cache.clear();
    PERSISTED_KEYS.forEach((key) => this.forget(key));
  }

  has(key: string): boolean {
    return this.cache.has(key) || this.restore(key) !== null;
  }

  private persist<T>(key: string, state: ListState<T>): void {
    if (!PERSISTED_KEYS.includes(key)) return;

    try {
      // Deliberately narrow: the applied filters, not the data they returned.
      const persisted = { search: state?.search ?? '', params: state?.params ?? {}, extraData: state?.extraData };
      localStorage.setItem(this.storageKey(key), JSON.stringify(persisted));
    } catch {
      // A full or unavailable storage (private browsing, quota) must never break the
      // board — the filters simply stop surviving a reload.
    }
  }

  private restore<T>(key: string): ListState<T> | null {
    if (!PERSISTED_KEYS.includes(key)) return null;

    try {
      const raw = localStorage.getItem(this.storageKey(key));
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      return { search: parsed?.search ?? '', params: parsed?.params ?? {}, extraData: parsed?.extraData } as ListState<T>;
    } catch {
      return null;
    }
  }

  private forget(key: string): void {
    if (!PERSISTED_KEYS.includes(key)) return;
    try {
      localStorage.removeItem(this.storageKey(key));
    } catch {
      // ignored — see persist()
    }
  }

  private storageKey(key: string): string {
    return `${STORAGE_PREFIX}${key}${this.scope ? `.${this.scope}` : ''}`;
  }
}
